import { Fragment, type JSX } from 'react';

/**
 * Renders a revealed secret (password / hidden custom field) with **digits in a
 * distinct color from letters**, so a reader can't confuse `O`/`0`, `I`/`1`, or
 * `l`/`1`. Letters and symbols keep the normal ink color; digit runs are tinted
 * with the brand accent. Monospace + fixed-width digits keep glyphs aligned.
 *
 * The value renders as text spans (not an <input>), which is what makes
 * per-character coloring possible. `data-hbs-secret` marks the container so the
 * full value is queryable in tests despite the split runs.
 */
export function SecretValue({ value, className = '' }: { value: string; className?: string }): JSX.Element {
  return (
    <span
      data-hbs-secret="true"
      className={`font-mono tracking-hbs-mono tabular-nums text-hbs-text ${className}`}
    >
      {splitDigitRuns(value).map((run, i) =>
        run.digit
          ? <span key={i} className="text-hbs-accent">{run.text}</span>
          : <Fragment key={i}>{run.text}</Fragment>,
      )}
    </span>
  );
}

/** Group a string into consecutive digit / non-digit runs (fewer spans). */
export function splitDigitRuns(value: string): { text: string; digit: boolean }[] {
  const runs: { text: string; digit: boolean }[] = [];
  for (const ch of value) {
    const digit = ch >= '0' && ch <= '9';
    const last = runs[runs.length - 1];
    if (last && last.digit === digit) last.text += ch;
    else runs.push({ text: ch, digit });
  }
  return runs;
}
